import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import {
  ablaufEndColumn,
  ablaufStartColumn,
  akaVeranstaltungIdColumn,
  expertsEditColumn,
  statusColumn,
  veranstaltungSapIdColumn,
} from '../../../components/general';
import { AusschreibungLink } from '../../../components/general/AusschreibungLink.tsx';
import {
  leistungIdColumn,
  leistungSingleRückmeldungColumn,
  statusAPColumn,
  terminartColumn,
  veranstaltungWoColumn,
} from '../../../components/general/TableColumns.tsx';
import { veranstaltungMultipleRückmeldungenColumn } from '../../../components/general/TableColumnsRückmeldung.tsx';
import { ausschreibungStatus, Leistung } from '../../../dtos.ts';
import { DataGridColDef, content as globalContent, printWithNoInfo } from '../../../utils';
import { getExperteNames } from '../../../utils/getBezeichnungen/getBezeichnungen.ts';
import {
  getAllLeistungen,
  getLowestAusschreibungStatus,
  getNumberOfUniqueAusschreibungStatuses,
} from '../../../utils/teilleistung.ts';
import { VeranstaltungFehlerCell } from './VeranstaltungFehlerCell.tsx';
import { LeistungSubGridElement, VeranstaltungElement } from './VeranstaltungListing.types.ts';

export const veranstaltungColumns = (): GridColDef<VeranstaltungElement>[] => {
  const columns: GridColDef<VeranstaltungElement>[] = [
    akaVeranstaltungIdColumn(),
    veranstaltungSapIdColumn(),
    leistungIdOrDetailPanelToggleColumn(),
    ablaufStartColumn(),
    ablaufEndColumn(),
    statusColumn(),
    statusAPColumn(
      (row) => getLowestAusschreibungStatus(row.teilleistungen),
      (row) => getNumberOfUniqueAusschreibungStatuses(row.teilleistungen),
    ),
    veranstaltungMultipleRückmeldungenColumn(),
    veranstaltungWoColumn(),
    expertsEditColumn(),
    terminartColumn(),
    fehlerColumn(),
  ];

  return columns;
};

export function leistungColumns(): GridColDef<LeistungSubGridElement>[] {
  return [
    leistungIndexColumn(), //
    leistungIdColumn((row) => row), //
    statusColumn(),
    statusAPColumn((row) => row.ausschreibung?.status ?? ausschreibungStatus.KEINE_AUSSCHREIBUNG),
    leistungSingleRückmeldungColumn(),
    leistungserbringerExperteColumn(),
  ];
}

/*****************************************
 * Veranstaltung table column definitions
 *****************************************/
function leistungIdOrDetailPanelToggleColumn<T extends VeranstaltungElement>(): GridColDef<T> {
  return {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
    flex: 0.5,
    resizable: true,
    renderHeader: () => globalContent.columnLabels.leistungId,
    renderCell: (params: GridRenderCellParams<VeranstaltungElement>) => {
      const leistungen = getAllLeistungen(params.row.teilleistungen);
      if (leistungen.length === 0) {
        return globalContent.noInfoAvailable;
      } else if (leistungen.length === 1) {
        return <AusschreibungLink leistung={leistungen[0]} />;
      } else {
        return GRID_DETAIL_PANEL_TOGGLE_COL_DEF.renderCell?.(params) ?? null;
      }
    },
  };
}

function fehlerColumn<T extends VeranstaltungElement>(): GridColDef<T> {
  return {
    field: 'fehler',
    headerName: globalContent.columnLabels.fehler,
    renderCell: (params) => <VeranstaltungFehlerCell {...params} />,
    flex: 0.4,
    valueGetter: () => 'fehler',
  };
}

/*****************************************
 * Leistung table column definitions
 *****************************************/
function leistungIndexColumn<T extends Leistung>(): DataGridColDef<T>[number] {
  return {
    field: 'leistungIndex',
    flex: 0.5,
    headerName: globalContent.columnLabels.shortIndex,
    valueGetter: (_, row) => row.index,
  };
}

function leistungserbringerExperteColumn<T extends Leistung>(): GridColDef<T> {
  return {
    field: 'experts',
    flex: 1,
    headerName: globalContent.columnLabels.experten,
    valueGetter: (_value, row): string =>
      printWithNoInfo(getExperteNames(row.leistungserbringer.map(({ experte }) => experte))),
  };
}
